import { useEffect, useState } from "react";
import { editorialCategories } from "../../../../api/editorialCategories.service";
import { getLegacyPublications } from "../../../../api/legacy-publications.service";
import { toPracticeOptions } from "./practiceLabel.js";
import type { PracticeOption, PublicationOption, RegionOption } from "./types.js";

type OptionsState<T> = {
  options: T[];
  loading: boolean;
  error: string | null;
};

function empty<T>(): OptionsState<T> {
  return { options: [], loading: false, error: null };
}

// Loads legacy publications for a given edition, labelled "{publication} ({edition})".
export function usePublicationOptions(edition: string): OptionsState<PublicationOption> {
  const [state, setState] = useState<OptionsState<PublicationOption>>(empty);

  useEffect(() => {
    const trimmed = edition.trim();
    if (!trimmed) {
      setState(empty());
      return;
    }
    let cancelled = false;
    setState({ options: [], loading: true, error: null });
    getLegacyPublications({ edition: trimmed })
      .then(resp => {
        if (cancelled) return;
        const options = resp.publications.map(p => ({
          id: p.id,
          name: `${p.publication} (${p.edition})`,
        }));
        setState({ options, loading: false, error: null });
      })
      .catch(err => {
        if (cancelled) return;
        setState({
          options: [],
          loading: false,
          error: err instanceof Error ? err.message : "Failed to load publications",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [edition]);

  return state;
}

// Loads regions (country editorial categories) for a publication. Guards against
// loading before a publication is chosen.
export function useRegionOptions(publicationId: number | null): OptionsState<RegionOption> {
  const [state, setState] = useState<OptionsState<RegionOption>>(empty);

  useEffect(() => {
    if (publicationId == null) {
      setState(empty());
      return;
    }
    let cancelled = false;
    setState({ options: [], loading: true, error: null });
    editorialCategories
      .getAll({ publicationId, type: "country", limit: 100 })
      .then(resp => {
        if (cancelled) return;
        const options = (resp.editorialCategories ?? []).map(cat => ({
          id: cat.id,
          name: cat.country || `Region ${cat.id}`,
        }));
        setState({ options, loading: false, error: null });
      })
      .catch(err => {
        if (cancelled) return;
        setState({
          options: [],
          loading: false,
          error: err instanceof Error ? err.message : "Failed to load regions",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [publicationId]);

  return state;
}

// Loads practice-area options for a publication + region. Guards against loading
// before both are chosen.
export function usePracticeOptions(
  publicationId: number | null,
  regionId: number | null,
): OptionsState<PracticeOption> {
  const [state, setState] = useState<OptionsState<PracticeOption>>(empty);

  useEffect(() => {
    if (publicationId == null || regionId == null) {
      setState(empty());
      return;
    }
    let cancelled = false;
    setState({ options: [], loading: true, error: null });
    editorialCategories
      .getAllPracticeAreas({ publicationId, countryId: regionId })
      .then(categories => {
        if (cancelled) return;
        setState({ options: toPracticeOptions(categories), loading: false, error: null });
      })
      .catch(err => {
        if (cancelled) return;
        setState({
          options: [],
          loading: false,
          error: err instanceof Error ? err.message : "Failed to load practices",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [publicationId, regionId]);

  return state;
}
